import React from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';


const formatVehicleType = (type) => {
  if (!type) return '—';
  return type
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

const OwnerVehiclesModal = ({ open, onClose, vehicleOwnerId, ownerName, vehicles = [] }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-lg w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-secondary">{t('pages.vehicle.title')}</h2>
            {ownerName && (
              <p className="text-sm text-gray-500 mt-0.5">{ownerName}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition"
            aria-label={t("common.close")}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto p-4 sm:p-6">
          {vehicles.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">No vehicles assigned.</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-2.5 text-left font-semibold text-secondary">Number</th>
                    <th className="px-4 py-2.5 text-left font-semibold text-secondary">Name</th>
                    <th className="px-4 py-2.5 text-left font-semibold text-secondary hidden sm:table-cell">{t('common.type')}</th>
                    <th className="px-4 py-2.5 text-right font-semibold text-secondary">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {vehicles.map((v) => (
                    <tr key={v.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                      <td className="px-4 py-2.5 font-medium text-orange-600">{v.vehicle_number}</td>
                      <td className="px-4 py-2.5 text-gray-700">{v.vehicle_name || '—'}</td>
                      <td className="px-4 py-2.5 text-gray-600 hidden sm:table-cell">
                        {formatVehicleType(v.vehicle_type)}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        {v.id ? (
                          <button
                            type="button"
                            onClick={() => {
                              onClose();
                              navigate(`/vehicles/view/${v.id}`);
                            }}
                            className="inline-flex items-center gap-1 text-orange-600 hover:text-orange-800 text-xs font-medium"
                          >
                            View
                            <ExternalLink className="w-3.5 h-3.5" />
                          </button>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="p-4 sm:p-6 border-t border-gray-200 flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm border border-gray-300 rounded-xl hover:bg-gray-50 text-secondary"
          >
            Close
          </button>
          {vehicleOwnerId && (
            <button
              type="button"
              onClick={() => {
                onClose();
                navigate(`/vehicle-owners/view/${vehicleOwnerId}`);
              }}
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-medium bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition"
            >
              View owner
              <ExternalLink className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OwnerVehiclesModal;
